import React, { useCallback, useState, useEffect } from 'react';
import { Card, Tab, Tabs, Button } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { RegisterNumbering } from '../../components/RegisterNumbering/RegisterNumbering';
import { RegisterNumberingEditor } from '../../components/RegisterNumbering/RegisterNumberingEditor';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { CreateUpdateRegisterNumberingDto } from '../../../common/spdCore/autogenerated/spdApiClient/models/CreateUpdateRegisterNumberingDto';
import { NewRegisterNumbering } from '../../../common/spdCore/contexts/RegisterNumberingContext';
import {
  GetHealthcareFacilityDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';

export const RegisterNumberingView: React.FC = () => {
  const { id: healthcareFacilityId } = useParams<{ id: string }>();
  const spd = useSpdCore();
  const [showEditor, setShowEditor] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [healthcareFacility, setHealthcareFacility] =
    useState<GetHealthcareFacilityDto | null>(null);

  useEffect(() => {
    if (!healthcareFacilityId) {
      return;
    }
    spd.healthCareFacilities
      .get(healthcareFacilityId)
      .then(facility => {
        setHealthcareFacility(facility);
      })
      .catch(error => {
        console.error('Błąd podczas pobierania placówki zdrowia:', error);
      });
  }, [healthcareFacilityId, spd.healthCareFacilities]);

  const getData = useCallback(
    async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
    ) => {
      if (!healthcareFacilityId) {
        return [];
      }

      try {
        return await spd.registerNumbering.findByHealthcareFacilityIdByPortion(
          healthcareFacilityId,
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
        );
      } catch (error) {
        console.error('Błąd podczas pobierania numeracji rejestrów:', error);
        throw error;
      }
    },
    [spd.registerNumbering, healthcareFacilityId],
  );

  const getDataCount = useCallback(
    async (filterText?: string) => {
      if (!healthcareFacilityId) {
        return 0;
      }

      try {
        return await spd.registerNumbering.findByHealthcareFacilityIdCount(
          healthcareFacilityId,
          filterText,
        );
      } catch (error) {
        console.error(
          'Błąd podczas pobierania liczby numeracji rejestrów:',
          error,
        );
        throw error;
      }
    },
    [spd.registerNumbering, healthcareFacilityId],
  );

  const handleCreateRegisterNumbering = async (
    data: CreateUpdateRegisterNumberingDto,
  ) => {
    if (!healthcareFacilityId) {
      throw ['Brak ID ośrodka zdrowia'];
    }

    const registerNumberingData = {
      ...data,
      healthcareFacilityId,
    };

    try {
      await spd.registerNumbering.create(registerNumberingData);
      setRefresh(!refresh);
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      throw errors;
    }
  };

  return (
    <>
      <Card>
        <Tabs defaultActiveKey="all" id="registerNumberings" className="mb-3">
          <Tab eventKey="all" title="Numeracje rejestrów" className="m-3">
            {healthcareFacility && (
              <>
                <strong>Numeracja dla placówki:</strong>{' '}
                {healthcareFacility.displayName}
              </>
            )}
            <RegisterNumbering
              getData={getData}
              getDataCount={getDataCount}
              refresh={refresh}
            />

            <Button
              variant="outline-primary"
              onClick={() => setShowEditor(true)}
            >
              Nowa numeracja
            </Button>
          </Tab>
        </Tabs>
      </Card>
      <RegisterNumberingEditor
        registerNumbering={NewRegisterNumbering(healthcareFacilityId)}
        show={showEditor}
        onHide={() => setShowEditor(false)}
        onSave={handleCreateRegisterNumbering}
      />
    </>
  );
};
